/**
 * Action-clock battles (Phase 59.5): one world to fight on. Pure.
 *
 * In real time nobody crosses space, so a battle on the action clock is fought on one planet:
 * the one most units deployed onto (ties → the largest). A unit that landed elsewhere comes
 * down on the battle world on the side facing where it came from — its own team's landing
 * spot, so arrival lanes still mean something — spread out round that spot. A setup whose
 * units already share a planet is left exactly as it is.
 */

import type { GameState, PlanetState, PlayerState, Vec3 } from '../types';
import { vecAdd, vecScale, vecSub, vecNormalize, vecCross, vecLength, surfaceTangentFrame } from '../math';
import { dsin, dcos, dacos } from './dmath';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { getSurfaceRadius } from './surface';
import { dryGroundNear, onLakeShore } from './settlements';
import { worldSheetOf } from './worldSheet';
import { onWheelsMainGround, routeSpacingArc } from './planetRoutes';
import { WEAPONS, EMPLACEMENT_WEAPONS, KNOCKBACK_RADIUS_FACTOR } from '../constants';

/** The planet an action battle is fought on. */
export function battlePlanetIndex(state: Pick<GameState, 'players' | 'planets'>): number {
  const counts = new Map<number, number>();
  for (const p of state.players) counts.set(p.planetIndex, (counts.get(p.planetIndex) ?? 0) + 1);
  let best = -1, bestCount = -1, bestRadius = -1;
  for (const [i, c] of [...counts].sort((a, b) => a[0] - b[0])) {
    const pl = state.planets[i];
    if (!pl || pl.type !== 'planet') continue;
    if (c > bestCount || (c === bestCount && pl.radius > bestRadius)) { best = i; bestCount = c; bestRadius = pl.radius; }
  }
  if (best >= 0) return best;
  // Nobody stands on a planet: the largest one
  return state.planets.reduce((bi, p, i, all) => p.type === 'planet' && (bi < 0 || p.radius > all[bi].radius) ? i : bi, -1);
}

/** Spots round `centre` on the sphere, `spacing` apart in a small spiral (deterministic). */
function spotsAround(centre: Vec3, count: number, spacing: number, radius: number): Vec3[] {
  const { tx, ty } = surfaceTangentFrame(centre);
  const out: Vec3[] = [];
  for (let k = 0; k < count; k++) {
    if (k === 0) { out.push(centre); continue; }
    const ring = Math.ceil(k / 6), a = k * 2.399963;   // golden-angle spiral
    const arc = ring * spacing / radius;
    const along = vecNormalize(vecAdd(vecScale(tx, dcos(a)), vecScale(ty, dsin(a))));
    out.push(vecNormalize(vecAdd(vecScale(centre, dcos(arc)), vecScale(along, dsin(arc)))));
  }
  return out;
}

/** Everyone on the battle world (see the file comment). A no-op when they already share one. */
export function gatherOnBattlePlanet(state: GameState): GameState {
  const bi = battlePlanetIndex(state);
  if (bi < 0 || state.players.every(p => p.planetIndex === bi)) return state;
  const world: PlanetState = state.planets[bi];
  const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
  const moved = new Map<number, Vec3>();
  // Where each team stands: the ones already on the battle world, then each arrival as it lands
  const anchors: Vec3[] = [];
  for (const t of teams) {
    const here = state.players.filter(p => p.teamIndex === t && p.planetIndex === bi);
    if (here.length > 0 && here.length === state.players.filter(p => p.teamIndex === t).length) {
      anchors.push(vecNormalize(here.reduce((s, p) => vecAdd(s, vecNormalize(p.surfaceDir)), { x: 0, y: 0, z: 0 })));
    }
  }
  const minSep = Math.min(8, Math.PI * world.radius * 0.5) / world.radius;   // radians
  for (const t of teams) {
    const away = state.players.filter(p => p.teamIndex === t && p.planetIndex !== bi).sort((a, b) => a.id - b.id);
    if (away.length === 0) continue;
    // The team's landing spot: its teammates already here, else the side facing the planet it came from
    const here = state.players.filter(p => p.teamIndex === t && p.planetIndex === bi);
    let anchor: Vec3;
    if (here.length > 0) anchor = vecNormalize(here.reduce((s, p) => vecAdd(s, vecNormalize(p.surfaceDir)), { x: 0, y: 0, z: 0 }));
    else {
      const from = state.planets[away[0].planetIndex];
      const d = from ? vecSub(from.position, world.position) : { x: 0, y: 0, z: 0 };
      anchor = vecLength(d) > 1e-9 ? vecNormalize(d) : vecNormalize(vecCross({ x: 0, y: 1, z: 0 }, { x: 1, y: 0, z: 0.37 * (t + 1) }));
    }
    // Never land on another team: push along the great circle away from the nearest one
    if (here.length === 0) {
      for (let pass = 0; pass < 4; pass++) {
        let nearest: Vec3 | null = null, best = Infinity;
        for (const a of anchors) { const d = dacos(Math.max(-1, Math.min(1, a.x * anchor.x + a.y * anchor.y + a.z * anchor.z))); if (d < best) { best = d; nearest = a; } }
        if (!nearest || best >= minSep) break;
        const off = vecSub(anchor, vecScale(nearest, nearest.x * anchor.x + nearest.y * anchor.y + nearest.z * anchor.z));
        const dir = vecLength(off) > 1e-9 ? vecNormalize(off) : surfaceTangentFrame(nearest).tx;
        anchor = vecNormalize(vecAdd(vecScale(nearest, dcos(minSep)), vecScale(dir, dsin(minSep))));
      }
    }
    anchors.push(anchor);
    const spots = spotsAround(anchor, here.length + away.length, 1.6, world.radius);
    away.forEach((p, i) => moved.set(p.id, spots[here.length + i]));
  }
  const players: PlayerState[] = state.players.map(p => {
    const dir = moved.get(p.id);
    return dir ? { ...p, planetIndex: bi, surfaceDir: dir, airborne: false, airbornePos: null, airborneVel: null } : p;
  });
  return { ...state, players };
}

/** A point `arc` u from `centre` toward the tangent direction `along` on a sphere of `radius`. */
function offsetOnSphere(centre: Vec3, along: Vec3, arc: number, radius: number): Vec3 {
  const a = arc / radius;
  return vecNormalize(vecAdd(vecScale(centre, dcos(a)), vecScale(along, dsin(a))));
}

/**
 * The person-scale battle layout (WORKBOARD §13, the S3 lever; off at classic).
 *
 * Default — the classic geometry in person units: every unit's arc from the battle's centre (between
 * the first two teams) shrinks by k, so separations, team spreads and the bearing between sides are
 * exactly what classic deploy gives, measured in person-heights. A duel stays a duel's distance, a
 * megaplanet skirmish a skirmish's.
 *
 * `frontHeights` given — an explicit front: two teams facing each other across that many
 * person-heights (more round a circle of that diameter), each team's units `deploySpacing` apart,
 * keeping the sides' bearing.
 *
 * Capture points, placed after this between the teams, follow. A no-op unless the profile asks for a person layout.
 */
export function layOutFront(state: GameState, frontHeights?: number): GameState {
  const ps = personSetOf(state);
  if (!ps.personLayout) return state;
  const bi = battlePlanetIndex(state);
  if (bi < 0) return state;
  const world = state.planets[bi];
  const teams = [...new Set(state.players.filter(p => p.planetIndex === bi).map(p => p.teamIndex))].sort((a, b) => a - b);
  if (teams.length === 0) return state;
  const anchorOf = (t: number) => vecNormalize(state.players.filter(p => p.teamIndex === t && p.planetIndex === bi)
    .reduce((acc, p) => vecAdd(acc, vecNormalize(p.surfaceDir)), { x: 0, y: 0, z: 0 }));
  const a0 = anchorOf(teams[0]);
  const a1 = teams.length > 1 ? anchorOf(teams[1]) : a0;
  const mid = vecAdd(a0, a1);
  const centre = vecLength(mid) > 1e-9 ? vecNormalize(mid) : a0;
  const moved = new Map<number, Vec3>();
  if (frontHeights === undefined) {
    // shrink every unit's arc from the centre by k
    for (const p of state.players) {
      if (p.planetIndex !== bi) continue;
      const d = vecNormalize(p.surfaceDir);
      const c = Math.max(-1, Math.min(1, centre.x * d.x + centre.y * d.y + centre.z * d.z));
      const theta = dacos(c);
      if (theta < 1e-12) { moved.set(p.id, d); continue; }
      const off = vecSub(d, vecScale(centre, c));
      const along = vecLength(off) > 1e-9 ? vecNormalize(off) : surfaceTangentFrame(centre).tx;
      moved.set(p.id, offsetOnSphere(centre, along, theta * world.radius * ps.k, world.radius));
    }
  } else {
    // the front's axis at the centre: from team 0 toward team 1
    const toward = vecSub(a1, vecScale(centre, centre.x * a1.x + centre.y * a1.y + centre.z * a1.z));
    const axis = vecLength(toward) > 1e-9 ? vecNormalize(toward) : surfaceTangentFrame(centre).tx;
    const lateral = vecNormalize(vecCross(centre, axis));
    const span = frontHeights * PERSON_BODY_HEIGHT * ps.k;
    teams.forEach((t, i) => {
      // two teams: team 0 at −axis, team 1 at +axis; more: round the circle
      const theta = Math.PI - (2 * Math.PI * i) / teams.length;
      const along = vecNormalize(vecAdd(vecScale(axis, dcos(theta)), vecScale(lateral, dsin(theta))));
      const laid = offsetOnSphere(centre, along, span / 2, world.radius);
      const spot = BASE_SHORE.on ? dryGroundNear(world, laid) : laid;   // 391: a side never lands in a lake (the front's own path: nothing slides here)
      const units = state.players.filter(p => p.teamIndex === t && p.planetIndex === bi).sort((a, b) => a.id - b.id);
      const spots = spotsAround(spot, units.length, ps.deploySpacing, world.radius);
      units.forEach((p, k) => moved.set(p.id, spots[k]));
    });
  }
  return { ...state, players: state.players.map(p => { const d = moved.get(p.id); return d ? { ...p, surfaceDir: d } : p; }) };
}

/**
 * 391 (74.3 step 2; the owner: "Bases move to shore"): a board base refuses a lake's floor as the settlements do (357's rule,
 * the lakes alone: in a lake or touching one; a playa is dry salt and keeps its base) — the first of the site rules
 * (`BASE_SITE_RULES`, so a fallback never leaves a base in water), on every world with a lake, not only a landform one. A
 * lake wider than the slide's reach sends the base to the nearest dry ground by the route graph that keeps the other rules
 * (`core/settlements.ts dryGroundNear`, a shore to the side of the line), and the nearer of that and the slide's spot wins:
 * the line runs the long way across a wide lake. Its people stand round it at the seeded spacing as before. On a megaplanet
 * lake floors are a quarter to a third of a wet world and a base stood on one on half the thalia and viridis seeds
 * (DECISIONS 391). Off the board, the explicit front's team spots (`layOutFront`) take `dryGroundNear` alone — nothing slides
 * there; the default (classic) layout, every unit's own arc, is left as it is. `on` false: the spots as laid (the A/B's before arm).
 */
export const BASE_SHORE = { on: true };

/** 69.6a: the board's share of the circumference between the bases, read from a setup, URL or CLI value — (0, ½], else none. */
export function boardSeparation(v: unknown): number | undefined {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) && n > 0 && n <= 0.5 ? n : undefined;
}

/**
 * 69.6a, the Foothold board (decisions 144, 145; the owner's lever: the base distance): the two bases `separation` of the
 * battle world's circumference apart. Their great circle runs through the deploy's two landings (so each seed keeps its
 * bearing): the bases stand either side of the landings' middle, half the separation each way, and each team's people
 * stand round their base `deploySpacing` apart. A third team or more: round the circle at the same arc from the middle.
 * The bases go into `state.board` (their buildings join them later in the setup). A no-op with no team on the battle world.
 */
export function layOutBoard(state: GameState, separation: number): GameState {
  const bi = battlePlanetIndex(state);
  if (bi < 0) return state;
  const world = state.planets[bi];
  const teams = [...new Set(state.players.filter(p => p.planetIndex === bi).map(p => p.teamIndex))].sort((a, b) => a - b);
  if (teams.length === 0) return state;
  const anchorOf = (t: number) => vecNormalize(state.players.filter(p => p.teamIndex === t && p.planetIndex === bi)
    .reduce((acc, p) => vecAdd(acc, vecNormalize(p.surfaceDir)), { x: 0, y: 0, z: 0 }));
  const a0 = anchorOf(teams[0]);
  const a1 = teams.length > 1 ? anchorOf(teams[1]) : a0;
  const mid = vecAdd(a0, a1);
  const centre = vecLength(mid) > 1e-9 ? vecNormalize(mid) : a0;
  const toward = vecSub(a1, vecScale(centre, centre.x * a1.x + centre.y * a1.y + centre.z * a1.z));
  const axis = vecLength(toward) > 1e-9 ? vecNormalize(toward) : surfaceTangentFrame(centre).tx;
  const lateral = vecNormalize(vecCross(centre, axis));
  const half = separation * Math.PI * world.radius;   // half the separation, in world units along the surface
  const spacing = personSetOf(state).deploySpacing, person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const moved = new Map<number, Vec3>();
  const bases = teams.map((t, i) => {
    // two teams: team 0 at −axis, team 1 at +axis (as layOutFront); more: round the circle
    const theta = Math.PI - (2 * Math.PI * i) / teams.length;
    const along = vecNormalize(vecAdd(vecScale(axis, dcos(theta)), vecScale(lateral, dsin(theta))));
    const dir = baseOnSite(world, centre, along, half, person);
    const units = state.players.filter(p => p.teamIndex === t && p.planetIndex === bi).sort((a, b) => a.id - b.id);
    spotsAround(dir, units.length, spacing, world.radius).forEach((d, k) => moved.set(units[k].id, d));
    return { team: t, planetIndex: bi, dir };
  });
  return {
    ...state,
    players: state.players.map(p => { const d = moved.get(p.id); return d ? { ...p, surfaceDir: d } : p; }),
    board: { separation, bases },
  };
}

/**
 * A rule a board base's spot must keep (74.13 step 7 and its follow-up): `applies` to the world at all, and `holds` at a
 * direction. layOutBoard takes the laid spot when every rule that applies holds there; else it slides the base along its
 * line (`baseOnSite`). In order of priority: where no spot within the slide keeps them all, the nearest that keeps the
 * longest run of them from the first wins.
 */
export interface BaseSiteRule {
  name: string;
  /** (the world sheet the lake rule reads is the ground's alone — 74.23: no seed since it was built once a ground) */
  applies(world: PlanetState): boolean;
  holds(world: PlanetState, dir: Vec3, person: number): boolean;
}

/** The site rules' dials: the slide's reach (mesh steps each way) and the yard the rise is read over (people). */
export const BASE_SITE = {
  slideSteps: 24,
  /** the yard: a disc this radius round the base (its HQ, pad and dump stand within ~25 people of it) */
  yardPeople: 25,
  /** the most the ground may rise across the yard (people). Calibrated on 92 bases (bd-1–30, the Story's 16): the ground
   * before the landforms rises 1–26 across a yard (one tephra base 64), a range's flank 31–66; at 18 the pads stand as they
   * did before the landforms (at most 35 people out), 17 bases sliding (median 79 people); at 12, 33 slide (to 825) */
  yardRisePeople: 18,
};

/** How far the ground rises across the disc of radius `r` (world units) round `dir`: highest less lowest of 25 samples. */
export function yardRise(world: PlanetState, dir: Vec3, r: number): number {
  const n = vecNormalize(dir), { tx, ty } = surfaceTangentFrame(n);
  let lo = getSurfaceRadius(world, n), hi = lo;
  for (const [ring, count] of [[1 / 3, 6], [2 / 3, 8], [1, 10]] as const) {
    for (let k = 0; k < count; k++) {
      const a = (2 * Math.PI * k) / count, t = vecAdd(vecScale(tx, dcos(a)), vecScale(ty, dsin(a)));
      const h = getSurfaceRadius(world, offsetOnSphere(n, t, ring * r, world.radius));
      lo = Math.min(lo, h); hi = Math.max(hi, h);
    }
  }
  return hi - lo;
}

/** The board base's site rules, in priority order (lane I's lake floor, DECISIONS 391, joins them). */
export const BASE_SITE_RULES: readonly BaseSiteRule[] = [
  // 391: off a lake's floor and the ring touching it (BASE_SHORE) — first, so a fallback never leaves a base in water
  { name: 'lake', applies: w => BASE_SHORE.on && worldSheetOf(w).basins.some(b => b.lake), holds: (w, d) => !onLakeShore(w, d) },
  // 74.13 step 7: on the wheels' main ground, so its vehicles can leave it (tf-1, pallor: an impact massif's ringed floor)
  { name: 'wheels', applies: w => w.baseGroundKey !== undefined, holds: (w, d) => onWheelsMainGround(w, d) },
  // the follow-up (the coordinator, Oct 31): off a range's flank — bd-2's base stood on a fold's, its pad shoved 47 people out
  { name: 'yard', applies: w => w.baseGroundKey !== undefined, holds: (w, d, person) => yardRise(w, d, BASE_SITE.yardPeople * person) <= BASE_SITE.yardRisePeople * person },
];

/**
 * A board base's spot, `half` from the centre along `along`: as laid where every site rule that applies to the world holds
 * there (BASE_SITE_RULES); else slid along that line a mesh step at a time (nearer first, then farther; at most
 * `BASE_SITE.slideSteps` each way) to the nearest spot where they all hold — or, none within the slide, the nearest that
 * keeps the longest run of them from the first (the laid spot on a tie). No rule applies (off the landforms): as laid.
 */
function baseOnSite(world: PlanetState, centre: Vec3, along: Vec3, half: number, person: number): Vec3 {
  const dir = offsetOnSphere(centre, along, half, world.radius);
  const rules = BASE_SITE_RULES.filter(r => r.applies(world));
  const kept = (d: Vec3) => { let k = 0; while (k < rules.length && rules[k].holds(world, d, person)) k++; return k; };
  let best = dir, bestKept = kept(dir);
  if (bestKept === rules.length) return dir;
  const step = routeSpacingArc(world) * world.radius;
  let slid: Vec3 | null = null;
  for (let m = 1; m <= BASE_SITE.slideSteps && !slid; m++) for (const sd of [-1, 1]) {
    const d = offsetOnSphere(centre, along, half + sd * m * step, world.radius), k = kept(d);
    if (k === rules.length) { slid = d; break; }
    if (k > bestKept) { best = d; bestKept = k; }
  }
  // 391: the spot as laid is in a lake — the graph's nearest dry ground that keeps the other rules (a shore to the side of the
  // line; failing that, the nearest dry ground at all) against the slide's spot: the nearer to the spot as laid wins, so a wide
  // lake never sends the base the long way along the line, and no fallback leaves it in water
  if (rules.length > 0 && rules[0].name === 'lake' && !rules[0].holds(world, dir, person)) {
    const others = rules.slice(1);
    let near = dryGroundNear(world, dir, d => others.every(r => r.holds(world, d, person)));
    if (onLakeShore(world, near)) near = dryGroundNear(world, dir);
    const a = (d: Vec3) => dacos(Math.max(-1, Math.min(1, d.x * dir.x + d.y * dir.y + d.z * dir.z)));
    if (!onLakeShore(world, near) && (!slid || a(near) < a(slid))) return near;
  }
  return slid ?? best;
}

/**
 * Decision 126 (the owner, Oct 10: "guns behind the lines"): where a team's guns stand at a person scale. A shell is
 * planet-family — its knockback reaches 9.6 u, about 175 people — and the teams land closer than that to each other,
 * so a gun among its own people has every target inside its own reach. The guns stand abreast behind the team, beyond
 * the heaviest gun shell's knockback reach of every one of its people, on the side away from the enemy; `spacing`
 * apart along the line (setup passes a standard shell's reach on a gun, so a shell on one gun does not reach the
 * next). The walk to a gun is a decision. `dump`: where the guns' shells wait (decision 128), `dumpBack` further back
 * along the same line from the team — out of the front's blasts, a short walk from the guns.
 */
export function gunsBehindTheLines(players: readonly PlayerState[], team: number, planetIndex: number, radius: number, count: number, spacing: number, dumpBack = 0): { guns: Vec3[]; dump: Vec3 | null } {
  const reach = Math.max(...EMPLACEMENT_WEAPONS.map(w => WEAPONS[w].blastRadius * KNOCKBACK_RADIUS_FACTOR));
  const line = lineBehindTheTeam(players, team, planetIndex, radius, count, spacing, reach);
  return line ? { guns: line.spots, dump: line.along(line.depth + dumpBack) } : { guns: [], dump: null };
}

/**
 * A line of `count` spots `spacing` apart behind a team on a planet — on the side away from the enemy, abreast across
 * the way back — pushed back until every spot is at least `clear` from every one of its people (decision 126's guns,
 * at their knockback reach; 63.1d's parked 6×6, clear of the spawn ring and the walk out of it). `along(u)`: the point
 * `u` back from the team along the line's centre; `back`: that way, at the team. Null when the team has nobody there.
 */
export function lineBehindTheTeam(players: readonly PlayerState[], team: number, planetIndex: number, radius: number, count: number, spacing: number, clear: number): { spots: Vec3[]; depth: number; back: Vec3; along: (u: number) => Vec3 } | null {
  const on = players.filter(p => p.planetIndex === planetIndex && p.alive);
  const own = on.filter(p => p.teamIndex === team);
  if (own.length === 0 || count <= 0) return null;
  const sum = (ps: readonly PlayerState[]) => ps.reduce((a, p) => vecAdd(a, vecNormalize(p.surfaceDir)), { x: 0, y: 0, z: 0 });
  const n = vecNormalize(sum(own));
  // back: the tangent at the team's landing pointing away from the enemy (no enemy on the planet: any tangent)
  const foes = on.filter(p => p.teamIndex !== team);
  const toFoe = foes.length > 0 ? vecSub(sum(foes), vecScale(n, sumDot(foes, n))) : null;
  const back = toFoe && vecLength(toFoe) > 1e-9 ? vecScale(vecNormalize(toFoe), -1) : surfaceTangentFrame(n).tx;
  const side = vecNormalize(vecCross(n, back));   // tangent all along the great circle n → back
  const reach = clear;
  const along = (from: Vec3, dir: Vec3, u: number) => vecNormalize(vecAdd(vecScale(from, dcos(u / radius)), vecScale(dir, dsin(u / radius))));
  const arcTo = (a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, a.x * b.x + a.y * b.y + a.z * b.z))) * radius;
  const ownDirs = own.map(p => vecNormalize(p.surfaceDir));
  // the line's centre: behind the rearmost person by the reach; then pushed back until every gun clears every person
  let depth = reach + Math.max(0, ...ownDirs.map(d => (d.x * back.x + d.y * back.y + d.z * back.z) * radius));
  let out: Vec3[] = [];
  for (let pass = 0; pass < 6; pass++) {
    const c = along(n, back, depth);
    out = Array.from({ length: count }, (_, k) => along(c, side, (k - (count - 1) / 2) * spacing));
    let nearest = Infinity;
    for (const g of out) for (const d of ownDirs) nearest = Math.min(nearest, arcTo(g, d));
    if (nearest >= reach) break;
    depth += reach - nearest + 1e-6;
  }
  return { spots: out, depth, back, along: (u: number) => along(n, back, u) };
}

function sumDot(ps: readonly PlayerState[], n: Vec3): number {
  let s = 0;
  for (const p of ps) { const d = vecNormalize(p.surfaceDir); s += d.x * n.x + d.y * n.y + d.z * n.z; }
  return s;
}

/**
 * 63.1d: of a few spots round `spot` — pushed back along `back` up to six people, and to either side up to six — the
 * first whose ground rises or falls no more than `grade` (people a person) within `reach` of it in any of eight ways,
 * else the flattest: a body parked to drive off is not parked on a hillside it cannot start up (a 6×6 left facing a
 * 0.4 grade crawled, read as stuck to its driver and backed into the crate behind it). The coarse ground.
 */
export function flatSpotNear(planet: PlanetState, spot: Vec3, back: Vec3, person: number, reach: number, grade: number): Vec3 {
  const n0 = vecNormalize(spot), side = vecNormalize(vecCross(n0, back));
  const at = (b: number, s: number) => vecNormalize(vecAdd(vecScale(n0, planet.radius), vecAdd(vecScale(back, b * person), vecScale(side, s * person))));
  const steepest = (d: Vec3): number => {
    const { tx, ty } = surfaceTangentFrame(d), h0 = getSurfaceRadius(planet, d);
    let worst = 0;
    for (let k = 0; k < 8; k++) {
      const a = (k / 8) * Math.PI * 2, way = vecAdd(vecScale(tx, dcos(a)), vecScale(ty, dsin(a)));
      worst = Math.max(worst, Math.abs(getSurfaceRadius(planet, vecNormalize(vecAdd(vecScale(d, planet.radius), vecScale(way, reach)))) - h0) / reach);
    }
    return worst;
  };
  let best = n0, bestGrade = Infinity;
  for (const b of [0, 2, 4, 6]) for (const s of [0, -3, 3, -6, 6]) {
    const d = at(b, s), g = steepest(d);
    if (g <= grade) return d;
    if (g < bestGrade) { best = d; bestGrade = g; }
  }
  return best;
}

